import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import { AppShell, CLIENT_NAV, LAWYER_NAV } from "./components/Layout";
import { Spinner } from "./components/ui";
import Login from "./pages/Login";
import Setup from "./pages/Setup";
import NotFound from "./pages/NotFound";
import Dashboard from "./pages/lawyer/Dashboard";
import Clients from "./pages/lawyer/Clients";
import ClientDetail from "./pages/lawyer/ClientDetail";
import Matters from "./pages/lawyer/Matters";
import MatterForm from "./pages/lawyer/MatterForm";
import MatterDetail from "./pages/lawyer/MatterDetail";
import Invoices from "./pages/lawyer/Invoices";
import InvoiceForm from "./pages/lawyer/InvoiceForm";
import InvoiceDetail from "./pages/lawyer/InvoiceDetail";
import Settings from "./pages/lawyer/Settings";
import PortalHome from "./pages/client/PortalHome";
import ClientMatterDetail from "./pages/client/ClientMatterDetail";
import ClientInvoices from "./pages/client/ClientInvoices";
import ClientProfile from "./pages/client/ClientProfile";
import { useTheme } from "./theme/ThemeContext";

function FullScreenSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg text-muted">
      <Spinner className="h-6 w-6" />
      <span className="sr-only">Loading</span>
    </div>
  );
}

function RequireAuth() {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function LawyerShell() {
  const { user, logout } = useAuth();
  const { firmName } = useTheme();
  if (!user || user.role !== "lawyer") return <Navigate to="/portal" replace />;
  return (
    <AppShell firmName={firmName} user={user} nav={LAWYER_NAV} onSignOut={() => void logout()}>
      <Outlet />
    </AppShell>
  );
}

function ClientShell() {
  const { user, logout } = useAuth();
  const { firmName } = useTheme();
  if (!user || user.role !== "client") return <Navigate to="/desk" replace />;
  return (
    <AppShell firmName={firmName} user={user} nav={CLIENT_NAV} onSignOut={() => void logout()}>
      <Outlet />
    </AppShell>
  );
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "lawyer" ? "/desk" : "/portal"} replace />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/setup" element={<Setup />} />
      <Route path="/" element={<RootRedirect />} />

      <Route element={<RequireAuth />}>
        <Route path="/desk" element={<LawyerShell />}>
          <Route index element={<Dashboard />} />
          <Route path="clients" element={<Clients />} />
          <Route path="clients/:id" element={<ClientDetail />} />
          <Route path="matters" element={<Matters />} />
          <Route path="matters/new" element={<MatterForm />} />
          <Route path="matters/:id" element={<MatterDetail />} />
          <Route path="matters/:id/edit" element={<MatterForm />} />
          <Route path="invoices" element={<Invoices />} />
          <Route path="invoices/new" element={<InvoiceForm />} />
          <Route path="invoices/:id" element={<InvoiceDetail />} />
          <Route path="invoices/:id/edit" element={<InvoiceForm />} />
          <Route path="settings" element={<Settings />} />
        </Route>

        <Route path="/portal" element={<ClientShell />}>
          <Route index element={<PortalHome />} />
          <Route path="matters/:id" element={<ClientMatterDetail />} />
          <Route path="invoices" element={<ClientInvoices />} />
          <Route path="profile" element={<ClientProfile />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
